import React from "react";
import { reverseGeocode, getLocationFromResults, getServiceArea } from "../utils/geo";

/**
 * Hook for handling map drag-to-search functionality.
 * Returns a callback that should be registered with MapContext.onDragEnd()
 *
 * Only triggers search if the geocoded location or service_area changes,
 * matching Marionette/Backbone shouldSearch() behavior. This prevents
 * unnecessary searches and marker flashing on subtle drags.
 *
 * Coords are always updated to keep map state in sync, even when search
 * doesn't trigger.
 *
 * Usage:
 *   const { handleDragEnd } = useMapSearch({ search, setParams, organization, params, mapContext });
 *   mapContext.setDragEnabled(true);
 *   mapContext.onDragEnd(handleDragEnd);
 *
 * The callback will only fire when drag is enabled via mapContext.setDragEnabled(true).
 */
function useMapSearch({ search, setParams, organization, params, mapContext }) {
  const handleDragEnd = React.useCallback(
    async (newCenter) => {
      try {
        // Save current zoom level (matches Marionette behavior - map.coffee:83)
        const currentZoom = mapContext.getZoom();
        mapContext.setMapZoom(currentZoom);

        const results = await reverseGeocode(newCenter);
        if (results && results.length > 0) {
          const location = getLocationFromResults(results, organization);
          const serviceArea = getServiceArea(results);

          // Check if location or service_area actually changed (shouldSearch logic)
          const hasLocationChanged = location !== params.location;
          const hasServiceAreaChanged = serviceArea !== params.service_area;

          if (hasLocationChanged || hasServiceAreaChanged) {
            // Location changed - update params and trigger search
            setParams({
              location,
              coords: newCenter,
              service_area: serviceArea,
            });

            search({
              location,
              coords: newCenter,
              service_area: serviceArea,
            });
          } else {
            // Location unchanged - just update coords silently
            setParams({
              coords: newCenter,
            });
          }
        }
      } catch (err) {
        // Reverse geocode failed; map center will update without location label
      }
    },
    [search, setParams, organization, params.location, params.service_area, mapContext]
  );

  return { handleDragEnd };
}

export { useMapSearch };
